import React, { useMemo, useRef, useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import SplitterLayout from 'react-splitter-layout'
import FileViewerGallery from './FileViewerGallery'
import type { FileViewerGalleryFile } from './FileViewerGallery'
import FileViewerDetails from './FileViewerDetails'
import FileViewerEditor, { type FileEditorSavePayload } from './FileViewerEditor'
import FileViewerButton from './FileViewerButton'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { hiddenFileInputStyle } from '../Dropzone'
import { clampGallerySize, DEFAULT_GALLERY_SIZE, GALLERY_SIZE_STORAGE_KEY, MIN_GALLERY_SIZE } from './gallerySize'

export type FileViewerFile = FileViewerGalleryFile

export interface FileViewerProps {
  files: FileViewerFile[]
  initialKey?: string
  /**
   * Called whenever a file's metadata changes (title, description, show-on).
   * Caller is responsible for persisting changes.
   */
  onFileChange: (file: FileViewerFile) => void

  /**
   * Called when the user finishes editing an image in the in-app editor.
   * The caller is responsible for uploading the resulting blob and returning
   * the updated QuoteFile (with new src and originalSrc stashed appropriately).
   */
  onEditSave: (payload: FileEditorSavePayload, file: FileViewerFile) => Promise<FileViewerFile> | FileViewerFile

  /**
   * Triggers the upload picker. Caller wires up the actual upload.
   */
  onUpload?: (files: File[]) => void

  /**
   * When supplied, renders an "add from provider" affordance in the gallery that
   * reopens a provider's own picker (e.g. CompanyCam) so more provider photos can
   * be added without leaving the viewer. `addProviderLabel` supplies the wording.
   */
  onAddProvider?: () => void
  /** Label/tooltip for the `onAddProvider` affordance, e.g. "Add from CompanyCam". */
  addProviderLabel?: string

  /**
   * When supplied, gallery thumbnails become drag-to-reorder. Called with the
   * full file list in its new order; the caller persists it. Selection is by
   * key, so the previewed file is preserved across a reorder.
   */
  onReorder?: (files: FileViewerFile[]) => void

  /**
   * Replace the currently selected file's content. Caller handles the upload.
   */
  onChangeFile?: (replacement: File, target: FileViewerFile) => void

  /**
   * Called instead of the file picker when Change is clicked on a provider-sourced
   * file (e.g. CompanyCam). Lets the caller reopen the provider's own picker rather
   * than the OS file dialog. Only used when the selected file has a `provider`.
   */
  onChangeProviderFile?: (target: FileViewerFile) => void

  onDelete?: (file: FileViewerFile) => void

  /**
   * Delete several files at once. Called from the multi-select bulk panel
   * after the user confirms. When omitted, multi-select is disabled — the
   * gallery's `Select all` button/menu won't render.
   */
  onDeleteMany?: (keys: string[]) => void

  /** Hide description + Show on. */
  titleOnly?: boolean

  /** Read-only — disables editing, deleting, replacing. */
  viewOnly?: boolean

  /**
   * Fires whenever the editor opens or closes. Parents (e.g. FileViewerDialog)
   * use this to swap their footer between "Cancel | Done" (gallery mode) and
   * a hidden footer (editor mode — the editor renders its own).
   */
  onEditingChange?: (editing: boolean) => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  splitter: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    '& .splitter-layout': {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    },
    '& .splitter-layout > .layout-pane': {
      position: 'relative',
      flex: '0 0 auto',
      overflow: 'hidden'
    },
    '& .splitter-layout > .layout-pane.layout-pane-primary': {
      flex: '1 1 auto'
    },
    '& .splitter-layout > .layout-splitter': {
      flex: '0 0 auto',
      width: 8,
      height: '100%',
      cursor: 'col-resize',
      background: theme.paintscout.palette.brand.Neutral.P96,
      borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      transition: 'background-color 120ms ease',
      '&:hover': {
        background: theme.paintscout.palette.brand.Neutral.P90
      }
    }
  },
  mobileStack: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  mobileDetails: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  mobileGallery: {
    flex: '0 0 auto',
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    background: theme.palette.common.white
  },
  bulkPanel: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.common.white
  },
  bulkHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2, 1, 2),
    minHeight: 44,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  bulkCount: {
    ...theme.typography.subtitle1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  bulkBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    gap: theme.spacing(2)
  },
  bulkActions: {
    display: 'flex',
    gap: theme.spacing(1)
  }
}))

function FileViewer(props: FileViewerProps) {
  const {
    files,
    initialKey,
    onFileChange,
    onEditSave,
    onUpload,
    onAddProvider,
    addProviderLabel,
    onReorder,
    onChangeFile,
    onChangeProviderFile,
    onDelete,
    onDeleteMany,
    titleOnly,
    viewOnly,
    onEditingChange
  } = props
  const classes = useStyles({})
  const isMobile = useIsMobile()
  const [selectedKey, setSelectedKey] = useState<string | undefined>(initialKey || files[0]?.key)
  const [editing, setEditing] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  // Persist the grey-bar (splitter) placement so a preferred — e.g. condensed —
  // thumbnail strip width sticks across opens. The desktop SplitterLayout mounts
  // fresh each open, so secondaryInitialSize reads the stored value on mount.
  const [gallerySize, setGallerySize] = useLocalStorage<number>(GALLERY_SIZE_STORAGE_KEY, DEFAULT_GALLERY_SIZE)
  // Holds the live size only while a drag is in progress. Starts undefined (no
  // mount-time snapshot) so a drag-end can never persist a stale value, and is
  // reset after each commit.
  const galleryDragSizeRef = useRef<number | undefined>()

  const selected = files.find((f) => f.key === selectedKey) || files[0]
  const inMultiSelectMode = selectedKeys.size > 0

  // Change button: provider-sourced files (e.g. CompanyCam) reopen the provider's
  // own picker; everything else opens the OS file dialog via the hidden input.
  const handleChangeClick = onChangeFile
    ? selected?.provider && onChangeProviderFile
      ? () => onChangeProviderFile(selected)
      : () => replaceInputRef.current?.click()
    : undefined
  const allFileKeys = useMemo(() => files.map((f) => f.key), [files])

  const setEditingAndNotify = (next: boolean) => {
    setEditing(next)
    onEditingChange?.(next)
  }

  const handleEditSave = async (payload: FileEditorSavePayload) => {
    if (!selected) return
    const updated = await onEditSave(payload, selected)
    onFileChange(updated)
    setEditingAndNotify(false)
  }

  const handleSelectAll = onDeleteMany
    ? () => {
        setSelectedKeys((prev) => {
          if (allFileKeys.length > 0 && allFileKeys.every((k) => prev.has(k))) {
            return new Set()
          }
          return new Set(allFileKeys)
        })
      }
    : undefined

  const handleToggleSelect = (file: FileViewerFile) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) {
        next.delete(file.key)
      } else {
        next.add(file.key)
      }
      return next
    })
  }

  const handleBulkDelete = () => {
    if (!onDeleteMany) return
    const keys = Array.from(selectedKeys)
    if (keys.length === 0) return
    onDeleteMany(keys)
    setSelectedKeys(new Set())
  }

  const handleClearSelection = () => setSelectedKeys(new Set())

  const bulkPanel = (
    <div className={classes.bulkPanel}>
      <div className={classes.bulkHeader}>
        <span className={classes.bulkCount}>{selectedKeys.size} selected</span>
      </div>
      <div className={classes.bulkBody}>
        <div>
          {selectedKeys.size === 1
            ? '1 file will be removed when you click Delete.'
            : `${selectedKeys.size} files will be removed when you click Delete.`}
        </div>
        <div className={classes.bulkActions}>
          <FileViewerButton onClick={handleClearSelection} tone="neutral">
            Cancel
          </FileViewerButton>
          <FileViewerButton onClick={handleBulkDelete} tone="danger">
            Delete
          </FileViewerButton>
        </div>
      </div>
    </div>
  )

  return (
    <div className={classes.root}>
      <div className={classes.splitter}>
        {editing && selected ? (
          // Full-bleed editor — gallery is hidden until the user clicks Back to files.
          <FileViewerEditor file={selected} onBack={() => setEditingAndNotify(false)} onSave={handleEditSave} />
        ) : isMobile ? (
          <div className={classes.mobileStack}>
            <div className={classes.mobileDetails}>
              {inMultiSelectMode ? (
                bulkPanel
              ) : selected ? (
                <FileViewerDetails
                  file={selected}
                  onChange={onFileChange}
                  onEdit={() => setEditingAndNotify(true)}
                  onChangeFile={handleChangeClick}
                  onDelete={onDelete ? () => onDelete(selected) : undefined}
                  titleOnly={titleOnly}
                  viewOnly={viewOnly}
                  compact
                />
              ) : null}
            </div>
            <div className={classes.mobileGallery}>
              <FileViewerGallery
                files={files}
                selectedKey={selected?.key}
                onSelect={(f) => {
                  setSelectedKey(f.key)
                }}
                onSelectAll={handleSelectAll}
                onUploadClick={onUpload ? () => uploadInputRef.current?.click() : undefined}
                onUploadFiles={onUpload}
                onAddProvider={onAddProvider}
                addProviderLabel={addProviderLabel}
                onReorder={onReorder}
                selectedKeys={selectedKeys}
                onToggleSelect={onDeleteMany ? handleToggleSelect : undefined}
                orientation="horizontal"
              />
            </div>
          </div>
        ) : (
          <SplitterLayout
            customClassName="file-viewer-splitter"
            primaryIndex={1}
            primaryMinSize={320}
            secondaryMinSize={MIN_GALLERY_SIZE}
            secondaryInitialSize={clampGallerySize(gallerySize)}
            onSecondaryPaneSizeChange={(size) => {
              galleryDragSizeRef.current = size
            }}
            onDragEnd={() => {
              if (galleryDragSizeRef.current === undefined) return
              setGallerySize(clampGallerySize(galleryDragSizeRef.current))
              galleryDragSizeRef.current = undefined
            }}
          >
            <FileViewerGallery
              files={files}
              selectedKey={selected?.key}
              onSelect={(f) => {
                setSelectedKey(f.key)
              }}
              onSelectAll={handleSelectAll}
              onUploadClick={onUpload ? () => uploadInputRef.current?.click() : undefined}
              onUploadFiles={onUpload}
              onAddProvider={onAddProvider}
              addProviderLabel={addProviderLabel}
              onReorder={onReorder}
              selectedKeys={selectedKeys}
              onToggleSelect={onDeleteMany ? handleToggleSelect : undefined}
            />
            {inMultiSelectMode ? (
              bulkPanel
            ) : selected ? (
              <FileViewerDetails
                file={selected}
                onChange={onFileChange}
                onEdit={() => setEditingAndNotify(true)}
                onChangeFile={handleChangeClick}
                onDelete={onDelete ? () => onDelete(selected) : undefined}
                titleOnly={titleOnly}
                viewOnly={viewOnly}
              />
            ) : (
              <div />
            )}
          </SplitterLayout>
        )}
      </div>

      {onUpload && (
        <input
          ref={uploadInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          style={hiddenFileInputStyle}
          onChange={(e) => {
            const list = e.target.files
            if (list && list.length) onUpload(Array.from(list))
            e.target.value = ''
          }}
        />
      )}
      {onChangeFile && (
        <input
          ref={replaceInputRef}
          type="file"
          accept="image/*,application/pdf"
          style={hiddenFileInputStyle}
          onChange={(e) => {
            const list = e.target.files
            if (list && list[0] && selected) onChangeFile(list[0], selected)
            e.target.value = ''
          }}
        />
      )}
    </div>
  )
}

export default FileViewer
